<template>
  <ul v-if="!tableLoading" class="header__filter header__filter--full-width">
    <li class="header__filter-item">
      <a
        ref="geoDataFieldLink"
        class="header__filter-link"
        @click="
          $refs.geoDataFieldContext.toggle(
            $refs.geoDataFieldLink,
            'bottom',
            'left',
            4
          )
        "
      >
        <i class="header__filter-icon iconoir-pin-alt"></i>
        <span class="header__filter-name">{{
          geoDataField
            ? geoDataField.name
            : $t('mapViewHeader.chooseGeoDataField')
        }}</span>
      </a>
      <Context ref="geoDataFieldContext" max-height-if-outside-viewport>
        <div class="map-view-header__geo-data-field-context">
          <FormGroup
            small-label
            :label="$t('mapViewHeader.geoDataField')"
            class="margin-bottom-2"
          >
            <Dropdown
              :value="view.geo_data_field"
              :disabled="
                readOnly ||
                !$hasPermission(
                  'database.table.view.update',
                  view,
                  database.workspace.id
                )
              "
              @input="updateGeoDataField"
            >
              <DropdownItem
                v-for="field in geoDataFields"
                :key="field.id"
                :icon="field._.type.iconClass"
                :name="field.name"
                :value="field.id"
              ></DropdownItem>
            </Dropdown>
          </FormGroup>
          <!-- No geo_data field exists yet in this table, guide the user to
               create one before a map view can plot anything. -->
          <Alert v-if="geoDataFields.length === 0" type="info">
            {{ $t('mapViewHeader.noGeoDataFieldHint') }}
          </Alert>
        </div>
      </Context>
    </li>
    <li class="header__filter-item">
      <a
        ref="customizeContextLink"
        class="header__filter-link"
        @click="
          $refs.customizeContext.toggle(
            $refs.customizeContextLink,
            'bottom',
            'left',
            4
          )
        "
      >
        <i class="header__filter-icon iconoir-settings"></i>
        <span class="header__filter-name">{{
          $t('mapViewHeader.customizePopup')
        }}</span>
      </a>
      <ViewFieldsContext
        ref="customizeContext"
        :database="database"
        :view="view"
        :fields="fields"
        :field-options="fieldOptions"
        @update-all-field-options="updateAllFieldOptions"
        @update-field-options-of-field="updateFieldOptionsOfField"
        @update-order="orderFieldOptions"
      ></ViewFieldsContext>
    </li>
    <li class="header__filter-item header__filter-item--full-width">
      <ViewSearch
        :view="view"
        :fields="fields"
        :store-prefix="storePrefix"
        :always-hide-rows-not-matching-search="true"
        @refresh="$emit('refresh', $event)"
      ></ViewSearch>
    </li>
  </ul>
</template>

<script>
import { mapState } from 'vuex'

import { notifyIf } from '@baserow/modules/core/utils/error'
import ViewFieldsContext from '@baserow/modules/database/components/view/ViewFieldsContext'
import ViewSearch from '@baserow/modules/database/components/view/ViewSearch'

export default {
  name: 'MapViewHeader',
  components: { ViewFieldsContext, ViewSearch },
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    view: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: true,
    },
    storePrefix: {
      type: String,
      required: true,
    },
  },
  emits: ['refresh'],
  computed: {
    ...mapState({
      tableLoading: (state) => state.table.loading,
    }),
    fieldOptions() {
      return this.$store.getters[
        this.storePrefix + 'view/map/getAllFieldOptions'
      ]
    },
    geoDataField() {
      return this.fields.find((field) => field.id === this.view.geo_data_field)
    },
    // Only geo_data typed fields can back a map view's marker position, so the
    // dropdown is restricted the same way the backend's `prepare_values` is.
    geoDataFields() {
      return this.fields.filter((field) => field.type === 'geo_data')
    },
  },
  methods: {
    async updateAllFieldOptions({ newFieldOptions, oldFieldOptions }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/map/updateAllFieldOptions',
          {
            newFieldOptions,
            oldFieldOptions,
            readOnly:
              this.readOnly ||
              !this.$hasPermission(
                'database.table.view.update_field_options',
                this.view,
                this.database.workspace.id
              ),
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async updateFieldOptionsOfField({ field, values, oldValues }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/map/updateFieldOptionsOfField',
          {
            field,
            values,
            oldValues,
            readOnly:
              this.readOnly ||
              !this.$hasPermission(
                'database.table.view.update_field_options',
                this.view,
                this.database.workspace.id
              ),
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async orderFieldOptions({ order }) {
      try {
        await this.$store.dispatch(
          this.storePrefix + 'view/map/updateFieldOptionsOrder',
          {
            order,
            readOnly:
              this.readOnly ||
              !this.$hasPermission(
                'database.table.view.update_field_options',
                this.view,
                this.database.workspace.id
              ),
          }
        )
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async updateGeoDataField(value) {
      try {
        await this.$store.dispatch('view/update', {
          view: this.view,
          values: { geo_data_field: value },
          readOnly: this.readOnly,
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
  },
}
</script>
